package linkerpangoquipdb

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String

public class VehicleCatalogQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> selectAllBrands(mapper: (
    id: Long,
    name: String,
    updated_at: Long,
  ) -> T): Query<T> = Query(-806_167_677, arrayOf("vehicle_brand"), driver, "VehicleCatalog.sq", "selectAllBrands", "SELECT vehicle_brand.id, vehicle_brand.name, vehicle_brand.updated_at FROM vehicle_brand ORDER BY name ASC") { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!
    )
  }

  public fun selectAllBrands(): Query<Vehicle_brand> = selectAllBrands(::Vehicle_brand)

  public fun <T : Any> selectBrandById(id: Long, mapper: (
    id: Long,
    name: String,
    updated_at: Long,
  ) -> T): Query<T> = SelectBrandByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!
    )
  }

  public fun selectBrandById(id: Long): Query<Vehicle_brand> = selectBrandById(id, ::Vehicle_brand)

  public fun <T : Any> selectModelsByBrandId(brand_id: Long, mapper: (
    id: Long,
    brand_id: Long,
    name: String,
    updated_at: Long,
  ) -> T): Query<T> = SelectModelsByBrandIdQuery(brand_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!
    )
  }

  public fun selectModelsByBrandId(brand_id: Long): Query<Vehicle_model> = selectModelsByBrandId(brand_id, ::Vehicle_model)

  public fun <T : Any> selectModelsByBrandName(name: String, mapper: (
    id: Long,
    brand_id: Long,
    name: String,
    updated_at: Long,
  ) -> T): Query<T> = SelectModelsByBrandNameQuery(name) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!
    )
  }

  public fun selectModelsByBrandName(name: String): Query<Vehicle_model> = selectModelsByBrandName(name, ::Vehicle_model)

  public fun <T : Any> selectAllModelsWithBrandName(mapper: (brand_name: String, model_name: String) -> T): Query<T> = Query(150_542_827, arrayOf("vehicle_brand", "vehicle_model"), driver, "VehicleCatalog.sq", "selectAllModelsWithBrandName", """
  |SELECT vb.name AS brand_name, vm.name AS model_name FROM vehicle_model vm
  |INNER JOIN vehicle_brand vb ON vm.brand_id = vb.id
  |ORDER BY vb.name ASC, vm.name ASC
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public fun selectAllModelsWithBrandName(): Query<SelectAllModelsWithBrandName> = selectAllModelsWithBrandName(::SelectAllModelsWithBrandName)

  /**
   * @return The number of rows updated.
   */
  public suspend fun upsertBrand(
    id: Long?,
    name: String,
    updated_at: Long,
  ): Long {
    val result = driver.execute(1_381_476_074, """INSERT OR REPLACE INTO vehicle_brand (id, name, updated_at) VALUES (?, ?, ?)""", 3) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindString(parameterIndex++, name)
          bindLong(parameterIndex++, updated_at)
        }.await()
    notifyQueries(1_381_476_074) { emit ->
      emit("vehicle_brand")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteAllBrands(): Long {
    val result = driver.execute(519_692_212, """DELETE FROM vehicle_brand""", 0).await()
    notifyQueries(519_692_212) { emit ->
      emit("vehicle_brand")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun upsertModel(
    id: Long?,
    brand_id: Long,
    name: String,
    updated_at: Long,
  ): Long {
    val result = driver.execute(1_391_548_044, """INSERT OR REPLACE INTO vehicle_model (id, brand_id, name, updated_at) VALUES (?, ?, ?, ?)""", 4) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindLong(parameterIndex++, brand_id)
          bindString(parameterIndex++, name)
          bindLong(parameterIndex++, updated_at)
        }.await()
    notifyQueries(1_391_548_044) { emit ->
      emit("vehicle_model")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteAllModels(): Long {
    val result = driver.execute(831_923_282, """DELETE FROM vehicle_model""", 0).await()
    notifyQueries(831_923_282) { emit ->
      emit("vehicle_model")
    }
    return result
  }

  private inner class SelectBrandByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("vehicle_brand", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("vehicle_brand", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-673_461_073, """SELECT vehicle_brand.id, vehicle_brand.name, vehicle_brand.updated_at FROM vehicle_brand WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, id)
    }

    override fun toString(): String = "VehicleCatalog.sq:selectBrandById"
  }

  private inner class SelectModelsByBrandIdQuery<out T : Any>(
    public val brand_id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("vehicle_model", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("vehicle_model", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(1_871_418_263, """SELECT vehicle_model.id, vehicle_model.brand_id, vehicle_model.name, vehicle_model.updated_at FROM vehicle_model WHERE brand_id = ? ORDER BY name ASC""", mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, brand_id)
    }

    override fun toString(): String = "VehicleCatalog.sq:selectModelsByBrandId"
  }

  private inner class SelectModelsByBrandNameQuery<out T : Any>(
    public val name: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("vehicle_model", "vehicle_brand", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("vehicle_model", "vehicle_brand", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_158_196_729, """
    |SELECT vm.id, vm.brand_id, vm.name, vm.updated_at FROM vehicle_model vm
    |INNER JOIN vehicle_brand vb ON vm.brand_id = vb.id
    |WHERE vb.name = ?
    |ORDER BY vm.name ASC
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, name)
    }

    override fun toString(): String = "VehicleCatalog.sq:selectModelsByBrandName"
  }
}
